// =============================================================================
// faceRecognition — thin wrapper over MediaPipe FaceLandmarker + SFace ONNX.
//
// Why this stack:
//   - Both @vladmandic/face-api 1.7.15 and @vladmandic/human 3.3.6 ship a
//     vintage of tfjs that writes to a JS prototype slot at module-eval. On
//     iOS 18.7/26.x Safari + Chrome 148 those slots are now frozen by the
//     engine for security; the write throws "Cannot assign to read only
//     property 'toString' of object '#<n>'" and the kiosk page can't load.
//     Diagnostic with a static import confirmed module-eval is the failure
//     site (not chunk transport). Repro on multiple devices = library-wide
//     issue, not a single device quirk.
//   - MediaPipe Tasks Vision is a thin JS shim over a WASM module —
//     module-eval doesn't touch JS prototypes at all. ONNX Runtime Web with
//     the /wasm sub-entry skips the jsep/webgpu loaders that hit a known
//     iOS 26 WebKit bug (upstream issue #26827).
//   - Self-hosted binaries: zero CDN dependency, CSP stays tight, kiosk
//     works fully offline once the first ~37MB is cached.
//
// Pipeline:
//   loadFaceModels() — initialises FaceLandmarker (478-point mesh + 6-point
//                       face outline) AND an ORT InferenceSession over the
//                       SFace MobileFaceNet (128-dim embedding).
//   detectSingleFace() — runs landmarker (numFaces:2 so we can reject
//                        multi-face), aligns to a 112×112 crop via the
//                        InsightFace 5-point ArcFace template, runs SFace
//                        to get a 128-d Float32Array, L2-normalises, returns
//                        DetectedFace with the same shape the kiosk + the
//                        enrolment panel expect.
//   matchDescriptor() — Euclidean L2 on L2-normalised vectors. Dimension
//                       guard skips ALL legacy generations (128-dim face-api
//                       AND 1024-dim Human) cleanly.
//
// Threshold notes:
//   - SFace published defaults: cosine 0.363 / L2 1.128 at same-person.
//     After post-hoc L2-normalisation, Euclidean ≡ √(2-2·cosine), so we
//     stay in L2 distance space and matchDescriptor's existing euclidean()
//     works unchanged.
//   - MATCH_THRESHOLD 1.0 + AMBIGUITY_GAP 0.1 are STARTING values, biased
//     toward false-reject per the existing kiosk philosophy (false-acceptance
//     = clocking the wrong worker is much worse). Must be calibrated against
//     a 10-pair same-person + 10-pair different-person sample on real kiosk
//     hardware before flipping production. See docs/face-calibration template.
//
// Alignment:
//   - SFace expects a 112×112 aligned face crop using the ArcFace 5-point
//     template ((38.2946,51.6963), (73.5318,51.5014), (56.0252,71.7366),
//     (41.5493,92.3655), (70.7299,92.2041)). The 5 source points come from
//     MediaPipe FaceLandmarker mesh indices: 263 (right-eye-outer),
//     33 (left-eye-outer), 4 (nose-tip), 308 (right-mouth-corner),
//     78 (left-mouth-corner). The affine warp is solved closed-form (6 vars,
//     5 pairs of (x,y) constraints = 10 equations → least-squares).
//   - Skipping or approximating alignment is the #1 cause of an SFace port
//     showing demo accuracy in dev and ~60% accuracy in production. Don't.
// =============================================================================

import { FaceLandmarker, FilesetResolver, type FaceLandmarkerResult } from '@mediapipe/tasks-vision'
import * as ort from 'onnxruntime-web/wasm'

const MODEL_BASE     = '/face-models-v2'
const WASM_MP_BASE   = '/wasm/mediapipe'
const WASM_ORT_BASE  = '/wasm/ort/'

// Combined budget for the four binaries on a cold kiosk: 3.6MB + 9.9MB +
// 10.6MB + 12.4MB ≈ 37MB. On packhouse-floor wifi this can take 30s;
// 60s timeout gives headroom + still fails loudly on a genuinely broken
// network instead of an indefinite spinner.
const MODEL_LOAD_TIMEOUT_MS = 60_000

function withRaceTimeout<T>(p: Promise<T>, label: string, ms: number): Promise<T> {
  return Promise.race([
    p,
    new Promise<T>((_, reject) => setTimeout(
      () => reject(new Error(`${label} timed out after ${Math.round(ms/1000)}s — check the network and refresh.`)),
      ms,
    )),
  ])
}

// Pinned in audit rows + bumped on every face-stack swap. The
// matchDescriptor() dimension guard ALSO discriminates by vector length —
// 128-dim face-api legacy + 1024-dim Human legacy + 128-dim SFace all
// coexist in the worker_face_embeddings table but only the current
// generation participates in matching.
// Generations:
//   'sface@2021dec'         — initial SFace deploy 2026-06-03 morning
//                              (wrong-aligned + over-normalised, embeddings
//                              were noise — false acceptance observed)
//   'sface@2021dec-aligned' — fixed swapped mesh indices, still had the
//                              /128.0 normalisation bug
//   'sface@2021dec-v2'      — fixed BOTH: correct alignment + raw 0-255 BGR
//                              (the form SFace's OpenCV reference actually
//                              feeds the model). This is the version that
//                              should produce well-separated same-person vs
//                              different-person distance distributions.
// The edge function's version-string filter (face-clock-in/roster handler)
// keeps every previous-generation vector out of the matcher. Workers
// previously enrolled on any old version must re-enrol once.
export const EMBEDDING_VERSION = 'sface@2021dec-v2'

// Public version string for audit display (diagnostics panel + match rows).
// Static — pinned to the current stack's package versions.
export const FACE_API_VERSION = 'sface@2021dec+mp-landmarker@0.10.35'

// Starting threshold + ambiguity gap. CALIBRATE before relying on these —
// the workflow's plan has a 10-pair-same / 10-pair-different procedure.
// Values here are intentionally conservative (tighter than SFace's
// published 1.128 L2 default) for false-reject-over-false-accept.
// Tuning history (2026-06-03, Thomas's calibration pass on the fixed
// alignment + correct BGR preprocessing pipeline):
//   1.0   — initial guess, way too loose: any face matched the sole
//           enrolled worker.
//   0.6   — set after first same-person measurement (0.4163) under
//           wrong-alignment. After fixing alignment + preprocessing, same-
//           person samples landed at 0.50 / 0.56 / 0.64 / 0.91 depending
//           on head pose, so 0.6 false-rejected anything beyond a square
//           front-on capture — kiosk oscillated CLOCKED ↔ NO_MATCH.
//   0.7   — captured the moderate-turn range (the 0.64 sample) but
//           false-rejected the 0.7–0.9 band: larger-than-moderate turns,
//           hats/glasses, and harsher kiosk lighting. Operators reported
//           it "a bit too tight".
//   0.8   — current (2026-06-05). Loosened one pose-band to accept those
//           real same-person captures, while still rejecting the 0.91
//           extreme-profile sample and staying well clear of the stranger
//           range (measured 1.375; conservative floor 0.9+). Margin to the
//           stranger floor stays ≥0.1; to the measured stranger ~0.58.
//           Bump further (≤0.85) only with match-log evidence — past ~0.85
//           the 0.91 extreme-profile / 0.9 stranger floor get close.
// AMBIGUITY_GAP unchanged (0.05) — only matters when ≥2 workers enrolled,
// and loosening it would risk confusing two enrolled workers; this change
// is about false-rejects (NO_MATCH), not ambiguity.
// The numbers live in faceBands.ts (no model imports there, so light screens
// can read them); re-exported here so existing importers are unchanged.
export { MATCH_THRESHOLD, AMBIGUITY_GAP, AUTO_ACCEPT_THRESHOLD, CONFIRM_MAX_DISTANCE,
         FAST_CLOCK_MAX_DISTANCE, FAST_CLOCK_MIN_GAP, distanceToConfidence } from '@/lib/faceBands'
import { MATCH_THRESHOLD, AMBIGUITY_GAP, FAST_CLOCK_MAX_DISTANCE, FAST_CLOCK_MIN_GAP } from '@/lib/faceBands'

// Kiosk decision bands (2026-06-10 face-clock improvement plan, item 4).
// The 0.7→0.8 loosening bought fewer false rejects by spending impostor
// margin. The confirm band buys it back: distances at or under
// AUTO_ACCEPT_THRESHOLD clock automatically (the original calibrated 0.7),
// while (AUTO_ACCEPT_THRESHOLD, CONFIRM_MAX_DISTANCE] surfaces the top
// candidates as a tap-to-confirm list instead of either silently accepting
// (the 0.8 risk) or rejecting (the 0.7 frustration). Past
// CONFIRM_MAX_DISTANCE nothing is offered — the measured stranger floor is
// ~0.9 (conservative) so 0.85 keeps a real margin below it.
// Tap-confirms are flagged to the server (confirm_method) so they can be
// audited separately from auto-accepts.

// Confidence-gated liveness (2026-06-18, kiosk): a match that is both very
// close AND clearly separated from the runner-up may skip the blink/turn
// challenge, because that is the band a spoof does not sit in — every marginal
// AUTO match and the whole CONFIRM band are still challenged. The kiosk uses
// it when its local liveness toggle is on; the piece-work modal uses it for a
// device whose liveness_mode is CONFIDENT (livenessPolicy.ts). ONE copy, here,
// beside the bands it is tighter than — both screens import it.
export function qualifiesForFastClock(distance: number, runnerUp: number | null): boolean {
  return distance <= FAST_CLOCK_MAX_DISTANCE
    && (runnerUp == null || (runnerUp - distance) >= FAST_CLOCK_MIN_GAP)
}

// MediaPipe FaceLandmarker mesh-index references for the 5-point alignment.
// These are the canonical ArcFace 5-point landmark positions, mapped onto
// MediaPipe's 478-point face mesh.
//
// Convention (subject's perspective, matches the ArcFace template):
//   MediaPipe index 33  = outer corner of subject's RIGHT eye (left side of
//                          un-mirrored camera frame). Confirmed against
//                          MediaPipe canonical_face_model + insightface
//                          arcface_align_norm 5-point pairing.
//   MediaPipe index 263 = outer corner of subject's LEFT eye (right side).
//   MediaPipe index 78  = subject's RIGHT mouth corner.
//   MediaPipe index 308 = subject's LEFT mouth corner.
//
// IMPORTANT: these four were ORIGINALLY mapped swapped (right ↔ left), which
// fed SFace a horizontally-mirrored 112×112 crop. The embeddings were still
// valid but lived in a non-canonical region of the embedding space — the
// effect is exactly what the workflow scoping warned about: ~60% accuracy
// in production despite good demo numbers. Visible to the operator as
// false-acceptance (strangers matching the one enrolled worker). Fixed
// 2026-06-03; embedding_version bumped to invalidate the old wrong-aligned
// vectors via matchDescriptor's dimension/version guard.
const MESH_RIGHT_EYE   = 33    // subject's right eye
const MESH_LEFT_EYE    = 263   // subject's left eye
const MESH_NOSE_TIP    = 4
const MESH_MOUTH_RIGHT = 78    // subject's right mouth corner
const MESH_MOUTH_LEFT  = 308   // subject's left mouth corner

// ArcFace alignment destination template (112×112), from
// insightface/recognition/arcface_torch/utils/utils_align.py.
const ARCFACE_DST: ReadonlyArray<readonly [number, number]> = [
  [38.2946, 51.6963],  // right_eye
  [73.5318, 51.5014],  // left_eye
  [56.0252, 71.7366],  // nose_tip
  [41.5493, 92.3655],  // right_mouth
  [70.7299, 92.2041],  // left_mouth
]

// -----------------------------------------------------------------------------
// FaceLandmarker singleton — GPU (WebGL) delegate first, CPU fallback.
//
// The landmarker is the per-frame cost floor: it runs on EVERY detect-loop
// frame (IDLE frames skip only the SFace embedding). On weak-CPU kiosk
// hardware (UROVO-class rugged scanners, A53-class cores) the CPU delegate
// is several times slower per frame than an Honor-class phone, which reads
// as "scanning is extremely slow" — the quality gates need several
// consecutive good frames. Even a weak Adreno GPU usually beats those cores,
// so we try GPU and degrade to CPU (the previous behaviour) when it fails —
// at create time OR at first detect (WebGL can be present but broken, and a
// long-running kiosk can lose its GL context mid-session).
// -----------------------------------------------------------------------------
let _landmarker: FaceLandmarker | null = null
let _landmarkerLoading: Promise<FaceLandmarker> | null = null
let _landmarkerDelegate: 'GPU' | 'CPU' = 'CPU'
// Sticky for the session: a device whose GPU delegate failed once stays on
// CPU — retrying GPU every rebuild would loop create/fail on broken drivers.
let _gpuBroken = false

// Operator kill switch (diagnostics panel): a device where GPU *misbehaves*
// rather than throwing (wrong/garbage landmarks) can be pinned to CPU
// without a build. localStorage so it survives kiosk reloads.
const FORCE_CPU_KEY = 'kf49_face_force_cpu'
function cpuForced(): boolean {
  try { return localStorage.getItem(FORCE_CPU_KEY) === '1' } catch { return false }
}

async function createLandmarker(delegate: 'GPU' | 'CPU'): Promise<FaceLandmarker> {
  const fileset = await FilesetResolver.forVisionTasks(WASM_MP_BASE)
  return FaceLandmarker.createFromOptions(fileset, {
    baseOptions: {
      modelAssetPath: `${MODEL_BASE}/face_landmarker.task`,
      delegate,
    },
    runningMode: 'IMAGE',
    // numFaces:2 lets us detect multi-face frames and reject them
    // explicitly in detectSingleFace. With numFaces:1 a multi-face frame
    // would silently surface the first detected face — wrong-worker risk.
    numFaces: 2,
    // Blendshapes are a near-free byproduct of inference; we read
    // eyeBlinkLeft/Right for active liveness (blink challenge). The
    // kiosk ignores them today — no behaviour change there.
    outputFaceBlendshapes: true,
    outputFacialTransformationMatrixes: false,
  })
}

async function activateLandmarker(delegate: 'GPU' | 'CPU'): Promise<FaceLandmarker> {
  const landmarker = await createLandmarker(delegate)
  _landmarker = landmarker
  _landmarkerDelegate = delegate
  console.log(`[face-recognition] FaceLandmarker ready (${delegate} delegate)`)
  return landmarker
}

async function initLandmarker(): Promise<FaceLandmarker> {
  if (!_gpuBroken && !cpuForced()) {
    try {
      return await activateLandmarker('GPU')
    } catch (e) {
      _gpuBroken = true
      console.warn('[face-recognition] GPU delegate unavailable, falling back to CPU:', e)
    }
  }
  return activateLandmarker('CPU')
}

// Tear down the active landmarker so the next ensureLandmarker() rebuilds it
// with whatever delegate the flags now select.
function discardLandmarker(): void {
  try { _landmarker?.close() } catch { /* GL context may already be gone */ }
  _landmarker = null
  _landmarkerLoading = null
}

async function ensureLandmarker(): Promise<FaceLandmarker> {
  if (_landmarker) return _landmarker
  if (!_landmarkerLoading) {
    console.log('[face-recognition] ensureLandmarker: starting')
    _landmarkerLoading = initLandmarker().catch(e => {
      // Don't cache a failed load — else the kiosk is stuck on "loading
      // model" until a full browser reload (an in-app retry would re-await
      // this same rejected promise). Clear it so the next attempt re-fetches.
      _landmarkerLoading = null
      throw e
    })
  }
  return _landmarkerLoading
}

// -----------------------------------------------------------------------------
// Per-frame timing — read by the kiosk diagnostics panel so device inference
// speed can be compared in the field instead of inferred from feel.
// -----------------------------------------------------------------------------
const TIMING_WINDOW = 30
const _landmarkMs: number[] = []  // landmarker.detect — every detect-loop frame
const _embedMs: number[] = []     // align + SFace — embedding frames only

function pushTiming(arr: number[], ms: number) {
  arr.push(ms)
  if (arr.length > TIMING_WINDOW) arr.shift()
}

// Rolling MATCH distance — the one number that says whether this device's
// frames actually resemble the templates enrolled elsewhere (audit 2026-09-15
// §4 #7). Anchor's "Spraying device" has never auto-accepted: its best-ever
// distance is 0.730, above the 0.70 auto cut, so every worker taps their name
// every shift — and the only way to learn that was to stand at the kiosk.
// Recorded here, reported on the roster call, readable in Labour → Devices.
//
// Fed by an EXPLICIT call from the scan loop, not from inside matchDescriptor:
// the enrolment panel's VERIFY step also matches (a person against their own
// six shots), and folding those into the median would make a well-behaved
// kiosk look better than it is.
const DISTANCE_WINDOW = 50
const _scanDistances: number[] = []

/** Note the top distance of one real scan against the roster. */
export function noteScanDistance(d: number): void {
  if (!Number.isFinite(d)) return
  _scanDistances.push(d)
  if (_scanDistances.length > DISTANCE_WINDOW) _scanDistances.shift()
}

/** Median, not mean: one stranger at 1.2 must not drag a healthy device's figure. */
function median(a: number[]): number | null {
  if (!a.length) return null
  const s = [...a].sort((x, y) => x - y)
  const mid = s.length >> 1
  const m = s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2
  return Math.round(m * 1000) / 1000
}

export type FaceDetectStats = {
  delegate:       'GPU' | 'CPU' | null   // null until the landmarker has loaded
  cpuForced:      boolean
  landmarkAvgMs:  number | null
  embedAvgMs:     number | null
  landmarkFrames: number                 // how many frames back the averages
  timingWindow:   number                 // look (≤ timingWindow)
  medianDistance: number | null          // rolling median top distance of real scans
  bestDistance:   number | null          // best (lowest) seen in the window
  distanceScans:  number                 // how many scans back those look
}

export function getFaceDetectStats(): FaceDetectStats {
  const avg = (a: number[]) => a.length ? Math.round(a.reduce((s, v) => s + v, 0) / a.length) : null
  return {
    delegate:       _landmarker ? _landmarkerDelegate : null,
    cpuForced:      cpuForced(),
    landmarkAvgMs:  avg(_landmarkMs),
    embedAvgMs:     avg(_embedMs),
    landmarkFrames: _landmarkMs.length,
    timingWindow:   TIMING_WINDOW,
    medianDistance: median(_scanDistances),
    bestDistance:   _scanDistances.length
      ? Math.round(_scanDistances.reduce((m, v) => (v < m ? v : m), Infinity) * 1000) / 1000
      : null,
    distanceScans:  _scanDistances.length,
  }
}

// Flip the force-CPU pin and rebuild the landmarker on the next detect frame.
// Un-forcing also clears the sticky GPU-broken flag so GPU gets a fresh try.
export function setFaceForceCpu(force: boolean): void {
  try {
    if (force) localStorage.setItem(FORCE_CPU_KEY, '1')
    else       localStorage.removeItem(FORCE_CPU_KEY)
  } catch { /* private mode — the in-session rebuild below still applies */ }
  if (!force) _gpuBroken = false
  discardLandmarker()
}

// -----------------------------------------------------------------------------
// SFace ONNX InferenceSession singleton
// -----------------------------------------------------------------------------
let _sface: ort.InferenceSession | null = null
let _sfaceLoading: Promise<ort.InferenceSession> | null = null

async function ensureSface(): Promise<ort.InferenceSession> {
  if (_sface) return _sface
  if (!_sfaceLoading) {
    console.log('[face-recognition] ensureSface: starting')
    _sfaceLoading = (async () => {
     try {
      // Point ORT at self-hosted WASM binaries. wasmPaths is the explicit
      // directory containing ort-wasm-simd-threaded.{wasm,mjs}.
      ort.env.wasm.wasmPaths = WASM_ORT_BASE
      // Main-thread WASM only. proxy:true would spawn a Worker (potential
      // SW-interception race); numThreads:1 avoids needing COOP+COEP headers
      // for SharedArrayBuffer.
      ort.env.wasm.proxy = false
      ort.env.wasm.numThreads = 1
      // 'error' not 'warning': SFace's exported ONNX model has its weights
      // declared as graph inputs (a known quirk of the OpenCV Zoo export
      // pipeline). At 'warning' level ORT logs ~150 "Initializer X appears
      // in graph inputs and will not be treated as constant value/weight"
      // messages per session — purely about a graph-optimization hint, the
      // model still runs correctly, and the noise drowns out our own
      // diagnostic match logs. The right long-term fix is to re-export the
      // model with remove_initializer_from_input.py, but for now we just
      // silence the warning class.
      ort.env.logLevel = 'error'
      const session = await ort.InferenceSession.create(
        `${MODEL_BASE}/face_recognition_sface_2021dec_int8.onnx`,
        { executionProviders: ['wasm'] },
      )
      _sface = session
      console.log('[face-recognition] SFace InferenceSession ready, input names:', session.inputNames, 'output names:', session.outputNames)
      return session
     } catch (e) {
       // Don't cache a failed load (see ensureLandmarker) — clear so the next
       // attempt re-fetches instead of re-awaiting this rejected promise.
       _sfaceLoading = null
       throw e
     }
    })()
  }
  return _sfaceLoading
}

// -----------------------------------------------------------------------------
// Public: loadFaceModels — initialises BOTH models behind one promise.
// -----------------------------------------------------------------------------
let modelsReady: Promise<void> | null = null

export function loadFaceModels(): Promise<void> {
  if (!modelsReady) {
    console.log('[face-recognition] loadFaceModels: starting')
    modelsReady = (async () => {
      const t0 = performance.now()
      try {
        await withRaceTimeout(
          Promise.all([ensureLandmarker(), ensureSface()]),
          'Face-model load',
          MODEL_LOAD_TIMEOUT_MS,
        )
        console.log('[face-recognition] all models ready in', Math.round(performance.now() - t0), 'ms')
      } catch (e) {
        console.error('[face-recognition] loadFaceModels failed:', e)
        modelsReady = null
        // On timeout the underlying ensure* promises may still be pending
        // forever (a stalled fetch never rejects), so clear the loading refs
        // too — an in-app retry then starts fresh instead of re-awaiting the
        // stuck promise. Resolved singletons (_landmarker/_sface) are kept.
        if (!_landmarker) _landmarkerLoading = null
        if (!_sface)      _sfaceLoading = null
        throw e
      }
    })()
  }
  return modelsReady
}

// -----------------------------------------------------------------------------
// Affine alignment — 5-point ArcFace template → 6-coefficient affine matrix.
//
// Solves (least squares) for [a,b,tx, c,d,ty] such that for each src pair
// (sx,sy) and dst pair (dx,dy):
//     dx = a·sx + b·sy + tx
//     dy = c·sx + d·sy + ty
// 5 pairs = 10 equations, 6 unknowns. Use the normal equations:
//     A · p = b   where p = [a,b,tx, c,d,ty]ᵀ
// closed-form 6×6 solve via Gaussian elimination — no external solver.
// -----------------------------------------------------------------------------
function solveAffine(
  src: ReadonlyArray<readonly [number, number]>,
  dst: ReadonlyArray<readonly [number, number]>,
): [number, number, number, number, number, number] | null {
  if (src.length !== dst.length || src.length < 3) return null
  // Build normal-equation matrices for [a,b,tx] (x-component) and
  // [c,d,ty] (y-component) separately — they're independent 3-var systems
  // sharing the same 3×3 sums matrix.
  let sxx = 0, sxy = 0, sx = 0
  let syy = 0, sy = 0, s1 = 0
  let bx0 = 0, bx1 = 0, bx2 = 0
  let by0 = 0, by1 = 0, by2 = 0
  for (let i = 0; i < src.length; i++) {
    const [x, y] = src[i]
    const [u, v] = dst[i]
    sxx += x * x
    sxy += x * y
    sx  += x
    syy += y * y
    sy  += y
    s1  += 1
    bx0 += x * u
    bx1 += y * u
    bx2 += u
    by0 += x * v
    by1 += y * v
    by2 += v
  }
  // 3×3 matrix M = [[sxx,sxy,sx],[sxy,syy,sy],[sx,sy,s1]]
  // Solve M·[a,b,tx]ᵀ = [bx0,bx1,bx2]ᵀ and M·[c,d,ty]ᵀ = [by0,by1,by2]ᵀ.
  const M = [
    [sxx, sxy, sx],
    [sxy, syy, sy],
    [sx,  sy,  s1],
  ]
  const solve3 = (rhs: [number, number, number]): [number, number, number] | null => {
    // Gaussian elimination on a 3×4 augmented matrix.
    const aug: number[][] = M.map((row, i) => [...row, rhs[i]])
    for (let col = 0; col < 3; col++) {
      // Partial pivot
      let piv = col
      for (let r = col + 1; r < 3; r++) if (Math.abs(aug[r][col]) > Math.abs(aug[piv][col])) piv = r
      if (Math.abs(aug[piv][col]) < 1e-9) return null
      if (piv !== col) [aug[piv], aug[col]] = [aug[col], aug[piv]]
      for (let r = col + 1; r < 3; r++) {
        const f = aug[r][col] / aug[col][col]
        for (let c = col; c < 4; c++) aug[r][c] -= f * aug[col][c]
      }
    }
    // Back-substitute
    const x = [0, 0, 0]
    for (let i = 2; i >= 0; i--) {
      let s = aug[i][3]
      for (let j = i + 1; j < 3; j++) s -= aug[i][j] * x[j]
      x[i] = s / aug[i][i]
    }
    return [x[0], x[1], x[2]]
  }
  const xs = solve3([bx0, bx1, bx2])
  const ys = solve3([by0, by1, by2])
  if (!xs || !ys) return null
  return [xs[0], xs[1], xs[2], ys[0], ys[1], ys[2]]
}

// -----------------------------------------------------------------------------
// Crop a 112×112 aligned face into an ImageData buffer.
//
// The affine maps src→dst, so to render dst we need the inverse: for each
// (u,v) in the 112×112 dst canvas, find the (x,y) in the source frame to
// sample. We solve a second 3×3 to get the inverse coefficients then use
// nearest-neighbour sampling for the 112² pixel walk — that's ~12K pixels,
// negligible compared to the SFace inference.
//
// Returns a Float32Array of NCHW shape [1,3,112,112] in BGR order with
// ((p - 127.5) / 128.0) normalisation, ready to feed into SFace.
// -----------------------------------------------------------------------------
function alignAndPreprocess(
  source: HTMLVideoElement | HTMLCanvasElement | HTMLImageElement,
  src: ReadonlyArray<readonly [number, number]>,
): {
  tensor:     Float32Array
  brightness: number
  sharpness:  number
  // RGBA pixel data for the aligned 112×112 crop — ready to write into a
  // canvas via ctx.putImageData() and then JPEG-encode for audit evidence
  // (backlog #5). Computed inline as a sibling of the BGR tensor write
  // so the kiosk doesn't pay for a second pass through the source canvas.
  alignedRgba: Uint8ClampedArray
} | null {
  // 1. Source frame dimensions — for video, use videoWidth/videoHeight; for
  // canvas/image, the natural width/height.
  const sw = (source as HTMLVideoElement).videoWidth
            ?? (source as HTMLImageElement).naturalWidth
            ?? (source as HTMLCanvasElement).width
  const sh = (source as HTMLVideoElement).videoHeight
            ?? (source as HTMLImageElement).naturalHeight
            ?? (source as HTMLCanvasElement).height
  if (!sw || !sh) return null

  // 2. Solve the inverse affine: dst → src, so we can sample src for each
  // dst pixel. We just solve it the other way round.
  const inverse = solveAffine(ARCFACE_DST, src)
  if (!inverse) return null
  const [a, b, tx, c, d, ty] = inverse

  // 3. Draw source onto an intermediate canvas so we can read pixel data.
  // OffscreenCanvas is preferred (no DOM mutation) but isn't in older
  // Safari (<16.4). Fall back to a hidden HTMLCanvasElement.
  const srcCanvas = (typeof OffscreenCanvas !== 'undefined')
    ? new OffscreenCanvas(sw, sh)
    : (() => {
        const el = document.createElement('canvas')
        el.width = sw
        el.height = sh
        return el
      })()
  const srcCtx = srcCanvas.getContext('2d') as
    OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D | null
  if (!srcCtx) return null
  // ts-ignore on drawImage — TS doesn't narrow the union for source param
  // perfectly; both ctx variants accept the same source types in practice.
  ;(srcCtx as CanvasRenderingContext2D).drawImage(source as CanvasImageSource, 0, 0, sw, sh)
  const srcImg = srcCtx.getImageData(0, 0, sw, sh)
  const srcData = srcImg.data

  // 4. For each pixel in the 112×112 dst, compute the src (x,y), sample
  // RGBA, write into the SFace tensor in BGR NCHW order with NO
  // normalisation — SFace expects raw uint8 BGR values cast to float
  // (verified against OpenCV's cv::FaceRecognizerSF::_preprocess() source:
  // blobFromImage(input, 1.0, Size(), Scalar(0,0,0), false, false, CV_32F)
  // — scalefactor 1.0, zero mean, no swapRB, no crop, CV_32F target).
  // The model handles scaling internally via BatchNorm. Dividing by 128.0
  // here previously fed the model values 100× smaller than expected,
  // producing essentially-random embeddings (visible to the operator as
  // strangers matching a sole enrolled worker more closely than the
  // worker matched themselves — 2026-06-03 finding).
  const NCHW = new Float32Array(1 * 3 * 112 * 112)
  const PLANE = 112 * 112
  // RGBA buffer for the aligned crop — same pixel walk as the tensor write,
  // so the extra cost is one cache line per pixel. The kiosk only converts
  // this to JPEG when a clock event resolves (backlog #5 — audit evidence).
  const RGBA = new Uint8ClampedArray(112 * 112 * 4)
  // Single pass also collects luminance stats for quality scoring:
  // mean and squared-mean → variance → sharpness proxy. Computing luma
  // inline costs nothing (we're already walking every pixel) but means
  // the enrolment quality check has data it can use without a second
  // canvas pass.
  let lumaSum = 0
  let lumaSqSum = 0
  for (let v = 0; v < 112; v++) {
    for (let u = 0; u < 112; u++) {
      const x = a * u + b * v + tx
      const y = c * u + d * v + ty
      const xi = Math.max(0, Math.min(sw - 1, Math.round(x)))
      const yi = Math.max(0, Math.min(sh - 1, Math.round(y)))
      const off = (yi * sw + xi) * 4
      const r = srcData[off]
      const g = srcData[off + 1]
      const bch = srcData[off + 2]
      const idx = v * 112 + u
      // Raw BGR, no normalisation. uint8 → float32 cast happens implicitly.
      NCHW[0 * PLANE + idx] = bch  // B
      NCHW[1 * PLANE + idx] = g    // G
      NCHW[2 * PLANE + idx] = r    // R
      // Aligned RGB(A) for the audit JPEG.
      const rgbaOff = idx * 4
      RGBA[rgbaOff]     = r
      RGBA[rgbaOff + 1] = g
      RGBA[rgbaOff + 2] = bch
      RGBA[rgbaOff + 3] = 255
      // ITU-R BT.601 luma (cheap; good enough for "is the crop bright/blurry?")
      const luma = 0.299 * r + 0.587 * g + 0.114 * bch
      lumaSum += luma
      lumaSqSum += luma * luma
    }
  }
  const N = PLANE
  const lumaMean = lumaSum / N
  const lumaVar  = (lumaSqSum / N) - (lumaMean * lumaMean)
  return { tensor: NCHW, brightness: lumaMean, sharpness: lumaVar, alignedRgba: RGBA }
}

// -----------------------------------------------------------------------------
// L2-normalise a 128-d Float32Array → number[].
// -----------------------------------------------------------------------------
function l2NormaliseToArray(v: Float32Array): number[] {
  let s = 0
  for (let i = 0; i < v.length; i++) s += v[i] * v[i]
  const norm = Math.sqrt(s) || 1
  const out = new Array<number>(v.length)
  for (let i = 0; i < v.length; i++) out[i] = v[i] / norm
  return out
}

// -----------------------------------------------------------------------------
// Public: detectSingleFace.
// -----------------------------------------------------------------------------
// The yaw entry in quality.issues. Exported so the pose-diverse enrolment
// wizard can recognise (and per-target override) the yaw gate without
// string-matching a copy that could drift.
export const YAW_ISSUE = 'Face the camera square-on'

export interface FaceQuality {
  // Frame-fraction of the face box height. <0.2 = subject too far; >0.85 = too close.
  coverage:     number
  // Approximate yaw deviation in [0, 1]. 0 = nose centred between eyes (front-on),
  // 1 = nose at eye-corner (extreme profile). >0.25 = "not square-on".
  yawDeviation: number
  // Eye-line tilt in degrees. 0 = level. |roll| > 25 = "head tilted".
  rollDegrees:  number
  // Mean luma of the aligned 112x112 crop (BT.601). [40-220] is the usable
  // band; outside is too dark / too bright.
  brightness:   number
  // Luma variance of the aligned crop (a cheap blur proxy — sharp images
  // have high variance because adjacent pixels differ). <300 = "blurry".
  sharpness:    number
  // Human-readable issues triggered for this capture. Empty when ok=true.
  issues:       string[]
  // Convenience — true only when all metrics fall inside the gate.
  ok:           boolean
}

export interface DetectedFace {
  descriptor: number[]                       // 128-float L2-normalised SFace embedding
  landmarks:  { noseTip: { x: number; y: number } }   // for liveness scoring (frame-space)
  box:        { x: number; y: number; width: number; height: number }
  quality:    FaceQuality
  // RGBA bytes (112×112×4) of the aligned crop. Held so callers can
  // synchronously hand to extractAlignedJpegBase64() on a CLOCKED event
  // without redoing the pixel walk. Always present (the kiosk currently
  // discards it on every non-clocked frame; carrying it costs ~50KB/frame
  // which is acceptable on the kiosk hardware).
  alignedRgba: Uint8ClampedArray
  // Active-liveness signals (for the blink / head-turn challenge).
  //   blink     — max(eyeBlinkLeft, eyeBlinkRight) blendshape, 0..1 (high =
  //               eyes closing). A blink is a high→low transition over frames.
  //   signedYaw — nose offset from the eye-line midpoint, signed, ~[-1,1].
  //               Magnitude = how far the head is turned (sign = which way,
  //               in raw-frame space; the display is CSS-mirrored). The turn
  //               challenge keys on |signedYaw| so it's mirror-agnostic.
  liveness: { blink: number; signedYaw: number }
}

export async function detectSingleFace(
  source: HTMLVideoElement | HTMLCanvasElement | HTMLImageElement,
  opts?: { skipEmbedding?: boolean },
): Promise<DetectedFace | null> {
  // Resolve source frame dims FIRST. MediaPipe's landmarker.detect throws
  // an INTERNAL "ROI width and height must be > 0" if the source has
  // zero dimensions — typically because the <video> element hasn't yet
  // produced a frame (still in HAVE_NOTHING / HAVE_METADATA), or the
  // camera stream stopped mid-loop. Guarding here is cheaper than letting
  // MediaPipe crash the whole detection thread.
  const sw = (source as HTMLVideoElement).videoWidth
            ?? (source as HTMLImageElement).naturalWidth
            ?? (source as HTMLCanvasElement).width
  const sh = (source as HTMLVideoElement).videoHeight
            ?? (source as HTMLImageElement).naturalHeight
            ?? (source as HTMLCanvasElement).height
  if (!sw || !sh) return null
  // For <video> specifically, also require readyState >= HAVE_CURRENT_DATA
  // so we never feed MediaPipe a freshly-attached stream that has metadata
  // but no decoded frame yet.
  if (source instanceof HTMLVideoElement && source.readyState < 2) return null

  // IDLE only needs to know a well-posed face is PRESENT (to enter IDENTIFY);
  // it never reads the embedding. On those frames we skip BOTH the SFace
  // inference AND the full-frame align/getImageData readback, which is the
  // bulk of the per-frame cost — roughly quartering it. That keeps the kiosk
  // tablet cool over a long shift (sustained CPU heat → thermal throttle →
  // slower scans for everyone in the queue). The landmarker still runs, so
  // pose/coverage/blink/turn signals are all available.
  const skipEmbedding = opts?.skipEmbedding === true

  let landmarker = await ensureLandmarker()
  const tLandmark = performance.now()
  let result: FaceLandmarkerResult
  try {
    result = landmarker.detect(source)
  } catch (e) {
    if (_landmarkerDelegate !== 'GPU') throw e
    // GPU delegate died at inference time (broken WebGL, lost GL context on a
    // long-running kiosk). Downgrade to CPU for the rest of the session and
    // retry this frame once — same degradation as a create-time GPU failure.
    console.warn('[face-recognition] GPU landmarker failed at detect(); rebuilding on CPU:', e)
    _gpuBroken = true
    discardLandmarker()
    landmarker = await ensureLandmarker()
    result = landmarker.detect(source)
  }
  pushTiming(_landmarkMs, performance.now() - tLandmark)
  if (!result || !result.faceLandmarks || result.faceLandmarks.length !== 1) return null
  const mesh = result.faceLandmarks[0]
  if (!mesh || mesh.length < 478) return null

  const unscale = (idx: number): [number, number] => [mesh[idx].x * sw, mesh[idx].y * sh]
  const src5: ReadonlyArray<readonly [number, number]> = [
    unscale(MESH_RIGHT_EYE),
    unscale(MESH_LEFT_EYE),
    unscale(MESH_NOSE_TIP),
    unscale(MESH_MOUTH_RIGHT),
    unscale(MESH_MOUTH_LEFT),
  ]

  // Frame-space face box from min/max of the 5 alignment landmarks (cheap
  // estimate; tighter than the full 478-point bounding rect, which can
  // include forehead drift).
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
  for (const [x, y] of src5) {
    if (x < minX) minX = x
    if (x > maxX) maxX = x
    if (y < minY) minY = y
    if (y > maxY) maxY = y
  }
  // Expand the box by ~30% to roughly match a face-detector rect for
  // ambient UI overlays.
  const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2
  const halfW = (maxX - minX) * 0.65
  const halfH = (maxY - minY) * 0.65
  const box = {
    x:      Math.max(0, cx - halfW),
    y:      Math.max(0, cy - halfH),
    width:  Math.min(sw, halfW * 2),
    height: Math.min(sh, halfH * 2),
  }
  const noseTip = { x: src5[2][0], y: src5[2][1] }

  // Pose geometry — landmark-only, so it's computed for skip-embedding frames
  // too (the IDLE entry gate reads coverage/yaw/roll directly).
  //
  // yawDeviation: how far the nose tip is from the eye-line midpoint,
  // normalised by inter-eye distance. Front-on faces have the nose near
  // the midpoint horizontally → near-zero deviation. Turned faces have
  // the nose offset toward the visible eye → deviation approaches 1.
  //
  // rollDegrees: angle of the eye-line. Atan2(dy, dx) of the eye-corner
  // segment, converted to degrees. Square eyes → 0°.
  const [rightEyeX, rightEyeY] = src5[0]
  const [leftEyeX,  leftEyeY]  = src5[1]
  const noseX = src5[2][0]
  const eyeDx = leftEyeX - rightEyeX
  const eyeDy = leftEyeY - rightEyeY
  const eyeDist = Math.hypot(eyeDx, eyeDy) || 1
  const eyeMidX = (rightEyeX + leftEyeX) / 2
  const yawDeviation = Math.min(1, Math.abs(noseX - eyeMidX) / (eyeDist * 0.5))
  const rollDegrees = Math.atan2(eyeDy, eyeDx) * 180 / Math.PI
  const coverage = box.height / sh

  // Liveness signals — also landmark-only. signedYaw is the signed cousin of
  // yawDeviation (abs()'d for the quality gate). blink reads the eye-closure
  // blendshapes (enabled in ensureLandmarker); 0 if blendshapes are absent.
  const signedYaw = Math.max(-1, Math.min(1, (noseX - eyeMidX) / (eyeDist * 0.5)))
  const bs = result.faceBlendshapes?.[0]?.categories
  const blinkL = bs?.find(c => c.categoryName === 'eyeBlinkLeft')?.score ?? 0
  const blinkR = bs?.find(c => c.categoryName === 'eyeBlinkRight')?.score ?? 0
  const blink  = Math.max(blinkL, blinkR)

  if (skipEmbedding) {
    // Presence + pose only. No descriptor, no brightness/sharpness — ok=false
    // so a skip frame can NEVER be mistaken for a match-ready capture by a
    // caller that forgets which mode it asked for. IDLE reads coverage/yaw/roll
    // directly, so an empty issues list / ok=false is fine there.
    const quality: FaceQuality = {
      coverage, yawDeviation, rollDegrees, brightness: 0, sharpness: 0,
      issues: [], ok: false,
    }
    return {
      descriptor:  [],
      landmarks:   { noseTip },
      box,
      quality,
      alignedRgba: new Uint8ClampedArray(0),
      liveness:    { blink, signedYaw },
    }
  }

  const sface = await ensureSface()
  const tEmbed = performance.now()
  // Align + preprocess for SFace. The aligner also returns brightness +
  // sharpness (computed inline as it walks every pixel) for quality scoring.
  const aligned = alignAndPreprocess(source, src5)
  if (!aligned) return null
  const { tensor, brightness, sharpness, alignedRgba } = aligned

  const inputName = sface.inputNames[0] ?? 'data'
  const outputName = sface.outputNames[0] ?? 'fc1'
  const inputTensor = new ort.Tensor('float32', tensor, [1, 3, 112, 112])
  const outputs = await sface.run({ [inputName]: inputTensor })
  pushTiming(_embedMs, performance.now() - tEmbed)
  const raw = outputs[outputName]
  if (!raw || !(raw.data instanceof Float32Array) || raw.data.length !== 128) {
    console.warn('[face-recognition] SFace output unexpected shape:', raw?.dims, 'len=', raw?.data?.length)
    return null
  }
  const descriptor = l2NormaliseToArray(raw.data)

  // Quality scoring — pose geometry (above) + crop stats. Cheap (no extra passes).
  const issues: string[] = []
  // Thresholds calibrated against the workflow's plan + typical kiosk
  // operating conditions. Tweak in the field if FRR or FAR move.
  if (coverage < 0.20)          issues.push('Move closer to the camera')
  else if (coverage > 0.85)     issues.push('Step back from the camera')
  if (yawDeviation > 0.25)      issues.push(YAW_ISSUE)
  if (Math.abs(rollDegrees) > 25) issues.push('Hold your head level')
  if (brightness < 40)          issues.push('Too dark — improve lighting')
  else if (brightness > 220)    issues.push('Too bright — reduce backlight')
  if (sharpness < 300)          issues.push('Image is blurry — hold still')

  const quality: FaceQuality = {
    coverage, yawDeviation, rollDegrees, brightness, sharpness,
    issues, ok: issues.length === 0,
  }

  return { descriptor, landmarks: { noseTip }, box, quality, alignedRgba, liveness: { blink, signedYaw } }
}

// -----------------------------------------------------------------------------
// matchDescriptor — pure math, unchanged from the Human era except the
// dimension-skip warning now names both legacy generations.
// -----------------------------------------------------------------------------
// Exported so the S2 re-verify sweep scores distances with the EXACT metric the
// live kiosk matcher uses (matchDescriptor below), and the two can never drift.
export function euclidean(a: number[], b: number[]): number {
  let s = 0
  for (let i = 0; i < a.length; i++) {
    const d = a[i] - b[i]
    s += d * d
  }
  return Math.sqrt(s)
}

export interface EnrolledVector {
  worker_number: string
  vector:        number[]
}

export interface MatchResult {
  outcome:           'CLOCKED' | 'NO_MATCH' | 'AMBIGUOUS'
  worker_number:     string | null
  top_distance:      number          // 0..~1.4 on L2-normalised vectors. lower = better
  runner_up_distance: number | null
  // Top-ranked per-worker candidates (best distance per worker, ascending).
  // The kiosk's confirm band reads these directly instead of `outcome`;
  // legacy callers (drain rematch, piecework gate) ignore them.
  candidates:        { worker_number: string; distance: number }[]
}

export function matchDescriptor(
  query: number[],
  enrolments: EnrolledVector[],
): MatchResult {
  const queryDim = query.length
  let skippedLegacy = 0
  const compatible: EnrolledVector[] = []
  for (const e of enrolments) {
    if (e.vector.length === queryDim) {
      compatible.push(e)
    } else {
      skippedLegacy++
    }
  }
  if (skippedLegacy > 0) {
    console.warn(`[face-recognition] skipped ${skippedLegacy} legacy vector(s) — incompatible dimension at match time. Affected workers must re-enrol on the current model (${EMBEDDING_VERSION}). Legacy generations: face-api 128-d (≠ SFace 128-d? unlikely collision), Human 1024-d.`)
  }

  if (compatible.length === 0) {
    return { outcome: 'NO_MATCH', worker_number: null, top_distance: Infinity, runner_up_distance: null, candidates: [] }
  }

  // Best distance per worker (multi-shot enrolment: take the best rep).
  const bestPerWorker = new Map<string, number>()
  for (const e of compatible) {
    const d = euclidean(query, e.vector)
    const prev = bestPerWorker.get(e.worker_number)
    if (prev === undefined || d < prev) bestPerWorker.set(e.worker_number, d)
  }

  const ranked = [...bestPerWorker.entries()]
    .map(([worker_number, distance]) => ({ worker_number, distance }))
    .sort((a, b) => a.distance - b.distance)

  const top        = ranked[0]
  const runnerUp   = ranked[1] ?? null
  const candidates = ranked.slice(0, 3)

  if (top.distance > MATCH_THRESHOLD) {
    return {
      outcome: 'NO_MATCH',
      worker_number: null,
      top_distance: top.distance,
      runner_up_distance: runnerUp?.distance ?? null,
      candidates,
    }
  }

  if (runnerUp && runnerUp.distance - top.distance < AMBIGUITY_GAP) {
    return {
      outcome: 'AMBIGUOUS',
      worker_number: null,
      top_distance: top.distance,
      runner_up_distance: runnerUp.distance,
      candidates,
    }
  }

  return {
    outcome: 'CLOCKED',
    worker_number: top.worker_number,
    top_distance: top.distance,
    runner_up_distance: runnerUp?.distance ?? null,
    candidates,
  }
}

// -----------------------------------------------------------------------------
// distanceToConfidence — unchanged formula; comment updated for SFace range.
// -----------------------------------------------------------------------------

// -----------------------------------------------------------------------------
// extractAlignedJpegBase64 — encode a DetectedFace's aligned crop as a base64
// JPEG, ready to POST to the face-clock-in edge function for audit storage
// (backlog #5). Returns null if the browser can't synthesise a JPEG (e.g.
// canvas APIs missing, taint). Quality default 0.6 → ~4-6KB per crop.
//
// Synchronous-feeling but async because canvas.toBlob is callback-driven.
// Never blocks the matcher rAF — only called on a CLOCKED outcome, by which
// point the rAF loop is already paused on the result screen.
// -----------------------------------------------------------------------------
export async function extractAlignedJpegBlob(
  face: DetectedFace,
  quality = 0.6,
): Promise<Blob | null> {
  if (!face.alignedRgba || face.alignedRgba.length !== 112 * 112 * 4) return null
  try {
    const canvas = (typeof OffscreenCanvas !== 'undefined')
      ? new OffscreenCanvas(112, 112)
      : (() => {
          const el = document.createElement('canvas')
          el.width = 112; el.height = 112
          return el
        })()
    const ctx = canvas.getContext('2d') as
      OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D | null
    if (!ctx) return null
    // Build via createImageData + .set() rather than `new ImageData(buf,…)`
    // — TS lib.dom narrows the constructor's first arg to ImageDataArray
    // (no SharedArrayBuffer), but face.alignedRgba's type is the wider
    // Uint8ClampedArray<ArrayBufferLike>. Going through createImageData
    // avoids the cast.
    const imgData = (ctx as CanvasRenderingContext2D).createImageData(112, 112)
    imgData.data.set(face.alignedRgba)
    ;(ctx as CanvasRenderingContext2D).putImageData(imgData, 0, 0)

    if (canvas instanceof OffscreenCanvas) {
      return await canvas.convertToBlob({ type: 'image/jpeg', quality })
    }
    return await new Promise<Blob | null>((resolve) =>
      (canvas as HTMLCanvasElement).toBlob(resolve, 'image/jpeg', quality)
    )
  } catch (e) {
    console.warn('[face-recognition] extractAlignedJpegBlob failed:', e)
    return null
  }
}

export async function extractAlignedJpegBase64(
  face: DetectedFace,
  quality = 0.6,
): Promise<string | null> {
  const blob = await extractAlignedJpegBlob(face, quality)
  if (!blob) return null
  const buf = new Uint8Array(await blob.arrayBuffer())
  let binary = ''
  for (let i = 0; i < buf.length; i++) binary += String.fromCharCode(buf[i])
  return btoa(binary)
}

// detectSingleFace works against an HTMLImageElement so we can re-run the
// matcher against an already-captured JPEG (queue drain path). Blob →
// data URL → Image element. Returned image must be disposed by the caller
// (revokeObjectURL).
export async function blobToImage(blob: Blob): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(blob)
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image()
    img.onload  = () => resolve(img)
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('blobToImage: image decode failed')) }
    img.src = url
  })
}

// -----------------------------------------------------------------------------
// embedAlignedCrop — SFace embedding of an ALREADY-ALIGNED 112×112 face crop
// (e.g. a stored evidence JPEG), skipping MediaPipe + re-alignment entirely.
//
// The S2 re-verification sweep re-scores each CLOCKED evidence crop against the
// claimed worker's enrolment. Because the crop is the exact 112×112 the kiosk
// fed SFace, feeding it straight through reproduces a comparable embedding —
// the tensor build MIRRORS alignAndPreprocess (NCHW, BGR planes, raw 0-255), so
// the result lives in the same space as the enrolment vectors. Returns a
// 128-float L2-normalised descriptor, or null on a bad crop / shape.
//
// NOTE: the evidence JPEG was encoded at quality 0.6, so this re-decode adds a
// small one-directional compression penalty — distances from here run slightly
// HIGHER than the kiosk's original top_distance for the same person. The S2
// audit cutoff is set well clear of that (fast-path clocks are gated ≤0.60; the
// flag cutoff is 1.0), so don't treat the two distances as bit-identical.
// -----------------------------------------------------------------------------
export async function embedAlignedCrop(
  source: HTMLImageElement | HTMLCanvasElement,
): Promise<number[] | null> {
  const sface = await ensureSface()
  const canvas = (typeof OffscreenCanvas !== 'undefined')
    ? new OffscreenCanvas(112, 112)
    : (() => { const el = document.createElement('canvas'); el.width = 112; el.height = 112; return el })()
  const ctx = canvas.getContext('2d') as
    OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D | null
  if (!ctx) return null
  ;(ctx as CanvasRenderingContext2D).drawImage(source as CanvasImageSource, 0, 0, 112, 112)
  const { data } = (ctx as CanvasRenderingContext2D).getImageData(0, 0, 112, 112)
  const PLANE = 112 * 112
  const nchw = new Float32Array(3 * PLANE)
  for (let idx = 0; idx < PLANE; idx++) {
    const o = idx * 4
    nchw[0 * PLANE + idx] = data[o + 2]  // B
    nchw[1 * PLANE + idx] = data[o + 1]  // G
    nchw[2 * PLANE + idx] = data[o]      // R
  }
  const inputName = sface.inputNames[0] ?? 'data'
  const outputName = sface.outputNames[0] ?? 'fc1'
  const outputs = await sface.run({ [inputName]: new ort.Tensor('float32', nchw, [1, 3, 112, 112]) })
  const raw = outputs[outputName]?.data
  if (!(raw instanceof Float32Array) || raw.length !== 128) return null
  return l2NormaliseToArray(raw)
}
